import React, { useRef, useEffect, useState } from 'react';
import { Button, List, Tag, Badge, Empty, Spin, Modal, Input, Select, Image, Tabs, Drawer } from 'antd';
import { SendOutlined, PaperClipOutlined, DeleteOutlined, MailOutlined, DownloadOutlined, FileOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useFirebaseAuthReady } from '../../hooks/useFirebaseAuthReady';
import type { InboxMessage, SendMessageRequest } from './models';
import { formatTimestamp } from './services';
import { useInbox, useCompose } from './handlers';

const { TextArea } = Input;

/* ═══════════════════ Message Item ═══════════════════ */

const MessageItem: React.FC<{
  msg: InboxMessage;
  isSent?: boolean;
  onClick: (msg: InboxMessage) => void;
  onHide?: (msgId: string) => void;
  onUnhide?: (msgId: string) => void;
}> = ({ msg, isSent, onClick, onHide, onUnhide }) => {
  const actions = [];
  if (!isSent && onHide) {
    actions.push(
      <Button
        key="hide"
        type="text"
        size="small"
        icon={<EyeInvisibleOutlined />}
        style={{ color: '#f59e0b', fontSize: 16 }}
        onClick={e => { e.stopPropagation(); onHide(msg.id); }}
        title="隱藏此對話"
      />
    );
  }
  if (onUnhide) {
    actions.push(
      <Button
        key="unhide"
        type="text"
        size="small"
        icon={<EyeOutlined />}
        style={{ color: '#3b82f6', fontSize: 16 }}
        onClick={e => { e.stopPropagation(); onUnhide(msg.id); }}
        title="取消隱藏"
      />
    );
  }

  return (
  <List.Item
    className={`cursor-pointer transition-colors ${!msg.isRead && !isSent ? 'bg-blue-50' : ''}`}
    onClick={() => onClick(msg)}
    actions={actions.length > 0 ? actions : undefined}
  >
    <List.Item.Meta
      avatar={
        <Badge dot={!msg.isRead && !isSent}>
          <MailOutlined style={{ fontSize: 20, color: msg.isRead || isSent ? '#d9d9d9' : '#1677ff' }} />
        </Badge>
      }
      title={
        <div className="flex items-center gap-2">
          <span className={`text-sm ${!msg.isRead && !isSent ? 'font-bold' : ''}`}>{msg.subject}</span>
          {(msg.images?.length > 0 || msg.attachments?.length > 0) && <PaperClipOutlined className="text-gray-400" />}
          {msg.threadId && <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>對話</Tag>}
        </div>
      }
      description={
        <div className="text-xs text-gray-400">
          <span>{isSent ? `寄給 ${msg.toUserName || msg.toUserId}` : `來自 ${msg.fromUserName || msg.fromUserId}`}</span>
          <span className="mx-2">·</span>
          <span>{formatTimestamp(msg.createdAt)}</span>
        </div>
      }
    />
    <div className="max-w-md">
      <div className="text-sm text-gray-600 whitespace-pre-line line-clamp-2">{msg.content}</div>
      <span className="text-xs text-blue-400">（點擊看完整內容）</span>
      {msg.images?.length > 0 && (
        <div className="flex gap-2 mt-2">
          <Image.PreviewGroup>
            {msg.images.map((img, i) => (
              <Image key={i} src={img.url} width={60} height={60} className="rounded object-cover" />
            ))}
          </Image.PreviewGroup>
        </div>
      )}
      {msg.attachments?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {msg.attachments.map((att, i) => (
            <a key={i} href={att.url} download={att.fileName} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs text-blue-600 hover:bg-blue-50">
              <DownloadOutlined />{att.fileName || '檔案'}
              {att.fileSize > 0 && <span className="text-gray-400">({(att.fileSize / 1024).toFixed(0)}KB)</span>}
            </a>
          ))}
        </div>
      )}
    </div>
  </List.Item>
  );
};

/* ═══════════════════ Compose Modal ═══════════════════ */

const ComposeModal: React.FC<{
  open: boolean;
  users: { userId: string; userName: string }[];
  replyTo: InboxMessage | null;
  images: { url: string; fileName: string; fileSize: number }[];
  attachments: { url: string; fileName: string; fileSize: number }[];
  sending: boolean;
  uploading: boolean;
  onClose: () => void;
  onSend: (req: SendMessageRequest) => void;
  onUpload: (file: File) => void;
  onUploadFile: (file: File) => void;
  onPaste: (e: ClipboardEvent) => void;
  onRemoveImage: (url: string) => void;
  onRemoveAttachment: (url: string) => void;
}> = ({ open, users, replyTo, images, attachments, sending, uploading, onClose, onSend, onUpload, onUploadFile, onPaste, onRemoveImage, onRemoveAttachment }) => {
  const [toUserId, setToUserId] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [content, setContent] = React.useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const attachFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      if (replyTo) {
        setToUserId(replyTo.fromUserId);
        setSubject(replyTo.subject.startsWith('Re: ') ? replyTo.subject : `Re: ${replyTo.subject}`);
      } else {
        setToUserId('');
        setSubject('');
      }
      setContent('');
    }
  }, [open, replyTo]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: Event) => onPaste(e as ClipboardEvent);
    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
  }, [open, onPaste]);

  const handleSubmit = () => {
    if (!toUserId || !subject.trim()) return;
    onSend({
      toUserId,
      subject: subject.trim(),
      content: content.trim(),
      images,
      parentId: replyTo?.id,
      threadId: replyTo?.threadId || replyTo?.id,
    });
  };

  return (
    <Modal
      title={replyTo ? '回覆信件' : '撰寫新信件'}
      open={open}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose}>取消</Button>,
        <Button key="send" type="primary" icon={<SendOutlined />}
          loading={sending} disabled={!toUserId || !subject.trim()}
          onClick={handleSubmit}>
          發送
        </Button>,
      ]}
    >
      <div className="space-y-3">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">收件人</label>
          <Select
            showSearch
            value={toUserId || undefined}
            onChange={setToUserId}
            placeholder="選擇收件人"
            optionFilterProp="label"
            className="w-full"
            disabled={!!replyTo}
            options={users.map(u => ({ value: u.userId, label: `${u.userName} (${u.userId})` }))}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">主旨</label>
          <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="信件主旨" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">內容（可直接 Ctrl+V 貼上圖片）</label>
          <TextArea
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={6}
            placeholder="輸入信件內容..."
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = '';
            }}
          />
          <Button icon={<PaperClipOutlined />} onClick={() => fileRef.current?.click()} loading={uploading}>
            上傳圖片
          </Button>
          <input
            ref={attachFileRef}
            type="file"
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) onUploadFile(f);
              e.target.value = '';
            }}
          />
          <Button icon={<FileOutlined />} onClick={() => attachFileRef.current?.click()} loading={uploading}>
            上傳檔案
          </Button>
          {uploading && <span className="text-xs text-gray-400">上傳中...</span>}
        </div>
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {images.map(img => (
              <div key={img.url} className="relative group">
                <Image src={img.url} width={80} height={80} className="rounded object-cover" />
                <Button
                  type="text" size="small" danger
                  icon={<DeleteOutlined />}
                  className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100"
                  onClick={() => onRemoveImage(img.url)}
                />
              </div>
            ))}
          </div>
        )}
        {attachments.length > 0 && (
          <div className="space-y-1">
            {attachments.map(att => (
              <div key={att.url} className="flex items-center gap-2 px-2 py-1 bg-gray-50 rounded text-sm">
                <FileOutlined className="text-gray-400" />
                <span className="flex-1 truncate">{att.fileName}</span>
                <span className="text-xs text-gray-400">{(att.fileSize / 1024).toFixed(0)}KB</span>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onRemoveAttachment(att.url)} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

/* ═══════════════════ Message Detail Drawer (Chat Style) ═══════════════════ */

const MessageDetail: React.FC<{
  msg: InboxMessage | null;
  isSent?: boolean;
  onClose: () => void;
}> = ({ msg, isSent, onClose }) => {
  const { user } = useAuth();
  const { ready: fbReady, uid: fbUid } = useFirebaseAuthReady();
  const [liveMsg, setLiveMsg] = useState<InboxMessage | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyImages, setReplyImages] = useState<{ url: string; fileName: string; fileSize: number }[]>([]);
  const [replyFiles, setReplyFiles] = useState<{ url: string; fileName: string; fileSize: number }[]>([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!msg?.id || !user?.userId) { setLiveMsg(null); return; }
    if (!fbReady || !fbUid) return;
    const coll = isSent ? 'sent' : 'messages';
    const ref = doc(db, 'inbox', user.userId, coll, msg.id);
    const unsub = onSnapshot(ref, snap => {
      if (snap.exists()) {
        setLiveMsg({ id: snap.id, ...snap.data() } as InboxMessage);
      }
    });
    return unsub;
  }, [msg?.id, user?.userId, isSent, fbReady, fbUid]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [liveMsg?.replies?.length]);

  useEffect(() => {
    setReplyText('');
    setReplyImages([]);
    setReplyFiles([]);
  }, [msg?.id]);

  const displayMsg = liveMsg || msg;

  useEffect(() => {
    if (!msg) return;
    const handler = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            setUploading(true);
            try {
              const { uploadImage } = await import('./services');
              const result = await uploadImage(file);
              setReplyImages(prev => [...prev, { url: result.url, fileName: result.fileName, fileSize: result.fileSize }]);
            } finally {
              setUploading(false);
            }
          }
          break;
        }
      }
    };
    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
  }, [msg]);

  const handleUploadImage = async (file: File) => {
    setUploading(true);
    try {
      const { uploadImage } = await import('./services');
      const result = await uploadImage(file);
      setReplyImages(prev => [...prev, { url: result.url, fileName: result.fileName, fileSize: result.fileSize }]);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadFile = async (file: File) => {
    setUploading(true);
    try {
      const { uploadFile } = await import('./services');
      const result = await uploadFile(file);
      setReplyFiles(prev => [...prev, { url: result.url, fileName: result.fileName, fileSize: result.fileSize }]);
    } finally {
      setUploading(false);
    }
  };

  const handleSendReply = async () => {
    if (!msg || (!replyText.trim() && replyImages.length === 0 && replyFiles.length === 0)) return;
    setSending(true);
    try {
      const { replyToMessage } = await import('./services');
      const recipientId = msg.fromUserId === user?.userId ? msg.toUserId : msg.fromUserId;
      await replyToMessage({
        messageId: msg.id,
        recipientId,
        content: replyText.trim(),
        images: replyImages,
        attachments: replyFiles,
      });
      setReplyText('');
      setReplyImages([]);
      setReplyFiles([]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Drawer
      title={displayMsg?.subject || ''}
      open={!!msg}
      onClose={onClose}
      width={560}
      styles={{ body: { display: 'flex', flexDirection: 'column', padding: 0 } }}
    >
      {displayMsg && (
        <>
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="bg-white rounded-lg">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span className="font-medium text-gray-700">{displayMsg.fromUserName || displayMsg.fromUserId}</span>
                <span className="text-xs">{formatTimestamp(displayMsg.createdAt)}</span>
              </div>
              <div className="mt-2 whitespace-pre-line text-sm text-gray-800 leading-relaxed">
                {displayMsg.content || <span className="text-gray-400">（無內容）</span>}
              </div>
              {displayMsg.images?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <Image.PreviewGroup>
                    {displayMsg.images.map((img, i) => (
                      <Image key={i} src={img.url} width={120} height={120} className="rounded object-cover" />
                    ))}
                  </Image.PreviewGroup>
                </div>
              )}
              {displayMsg.attachments?.length > 0 && (
                <div className="space-y-2 mt-2">
                  {displayMsg.attachments.map((att, i) => (
                    <a key={i} href={att.url} download={att.fileName} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border hover:bg-blue-50 hover:border-blue-200 transition-colors">
                      <FileOutlined className="text-blue-500 text-lg" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-700 truncate">{att.fileName || '未知檔案'}</div>
                        {att.fileSize > 0 && <div className="text-xs text-gray-400">{(att.fileSize / 1024).toFixed(0)} KB</div>}
                      </div>
                      <DownloadOutlined className="text-gray-400" />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {displayMsg.replies?.map((r, i) => {
              const isMe = r.fromUserId === user?.userId;
              return (
                <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg p-3 ${isMe ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-1 gap-4">
                      <span className={`text-xs font-medium ${isMe ? 'text-blue-600' : 'text-gray-600'}`}>
                        {isMe ? '我' : (r.fromUserName || r.fromUserId)}
                      </span>
                      <span className="text-xs text-gray-400">{formatTimestamp(r.createdAt)}</span>
                    </div>
                    {r.content && <div className="text-sm text-gray-800 whitespace-pre-line">{r.content}</div>}
                    {r.images?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Image.PreviewGroup>
                          {r.images.map((img, j) => (
                            <Image key={j} src={img.url} width={80} height={80} className="rounded object-cover" />
                          ))}
                        </Image.PreviewGroup>
                      </div>
                    )}
                    {r.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {r.attachments.map((att, j) => (
                          <a key={j} href={att.url} download={att.fileName} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-xs text-blue-600 border hover:bg-blue-50">
                            <DownloadOutlined />{att.fileName || '檔案'}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={chatEndRef} />
          </div>

          <div className="border-t p-4 space-y-2 bg-gray-50">
            <Input.TextArea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              rows={3}
              placeholder="輸入回覆內容...（可直接 Ctrl+V 貼圖）"
              onPressEnter={e => { if (e.ctrlKey) handleSendReply(); }}
            />
            {replyImages.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {replyImages.map(img => (
                  <div key={img.url} className="relative group">
                    <Image src={img.url} width={60} height={60} className="rounded object-cover" />
                    <Button type="text" size="small" danger icon={<DeleteOutlined />}
                      className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100"
                      onClick={() => setReplyImages(prev => prev.filter(i => i.url !== img.url))} />
                  </div>
                ))}
              </div>
            )}
            {replyFiles.length > 0 && (
              <div className="space-y-1">
                {replyFiles.map(f => (
                  <div key={f.url} className="flex items-center gap-2 px-2 py-1 bg-white rounded text-sm border">
                    <FileOutlined className="text-gray-400" />
                    <span className="flex-1 truncate">{f.fileName}</span>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />}
                      onClick={() => setReplyFiles(prev => prev.filter(a => a.url !== f.url))} />
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input ref={imgRef} type="file" accept="image/*" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadImage(f); e.target.value = ''; }} />
              <input ref={fileRef} type="file" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadFile(f); e.target.value = ''; }} />
              <Button icon={<PaperClipOutlined />} size="small" onClick={() => imgRef.current?.click()} loading={uploading}>圖片</Button>
              <Button icon={<FileOutlined />} size="small" onClick={() => fileRef.current?.click()} loading={uploading}>檔案</Button>
              <div className="flex-1" />
              <span className="text-xs text-gray-400">Ctrl+Enter 發送</span>
              <Button type="primary" icon={<SendOutlined />} size="small" loading={sending}
                disabled={!replyText.trim() && replyImages.length === 0 && replyFiles.length === 0}
                onClick={handleSendReply}>
                回覆
              </Button>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

/* ═══════════════════ Main Page ═══════════════════ */

const InboxPage: React.FC = () => {
  const inbox = useInbox();
  const compose = useCompose();
  const [viewMsg, setViewMsg] = useState<InboxMessage | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const prevTopMsgId = useRef<string | null>(null);

  const currentMessages = inbox.tab === 'inbox'
    ? inbox.messages.filter(m => !m.isHidden)
    : inbox.tab === 'hidden'
    ? inbox.messages.filter(m => m.isHidden)
    : inbox.sentMessages;

  // Scroll to top when a new message arrives (top message ID changes)
  useEffect(() => {
    const topId = currentMessages[0]?.id ?? null;
    if (prevTopMsgId.current !== null && topId !== prevTopMsgId.current) {
      listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevTopMsgId.current = topId;
  }, [currentMessages]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-end mb-3">
        <Button type="primary" icon={<SendOutlined />} onClick={() => compose.openCompose()}>
          撰寫新信件
        </Button>
      </div>

      <div ref={listRef} className="bg-white rounded-xl shadow-sm border">
        <Tabs
          activeKey={inbox.tab}
          onChange={k => inbox.setTab(k as 'inbox' | 'sent' | 'hidden')}
          className="px-4"
          items={[
            {
              key: 'inbox',
              label: <Badge count={inbox.messages.filter(m => !m.isRead && !m.isHidden).length} offset={[10, 0]}>收件匣 ({inbox.messages.filter(m => !m.isHidden).length})</Badge>,
            },
            {
              key: 'hidden',
              label: <span className="flex items-center gap-1"><EyeInvisibleOutlined />已隱藏 ({inbox.messages.filter(m => m.isHidden).length})</span>,
            },
          ]}
        />
        {inbox.loading ? (
          <div className="flex justify-center py-12"><Spin size="large" /></div>
        ) : currentMessages.length === 0 ? (
          <Empty description={inbox.tab === 'inbox' ? '沒有信件' : inbox.tab === 'hidden' ? '沒有已隱藏的對話' : '沒有寄出的信件'} className="py-12" />
        ) : (
          <List
            dataSource={currentMessages}
            renderItem={msg => (
              <MessageItem
                key={msg.id}
                msg={msg}
                isSent={inbox.tab === 'sent'}
                onHide={inbox.tab === 'inbox' ? async (msgId) => {
                  const { hideMessage } = await import('./services');
                  await hideMessage(msgId);
                } : undefined}
                onUnhide={inbox.tab === 'hidden' ? async (msgId) => {
                  const { unhideMessage } = await import('./services');
                  await unhideMessage(msgId);
                } : undefined}
                onClick={(m) => { setViewMsg(m); if (!m.isRead && inbox.tab !== 'sent') inbox.markAsRead(m.id); }}
              />
            )}
          />
        )}
      </div>

      <ComposeModal
        open={compose.open}
        users={compose.users}
        replyTo={compose.replyTo}
        images={compose.images}
        attachments={compose.attachments}
        sending={compose.sending}
        uploading={compose.uploading}
        onClose={compose.closeCompose}
        onSend={compose.handleSend}
        onUpload={compose.handleUploadImage}
        onUploadFile={compose.handleUploadFile}
        onPaste={compose.handlePaste}
        onRemoveImage={compose.removeImage}
        onRemoveAttachment={compose.removeAttachment}
      />

      <MessageDetail
        msg={viewMsg}
        isSent={inbox.tab === 'sent'}
        onClose={() => setViewMsg(null)}
      />
    </div>
  );
};

export default InboxPage;
